// approver-onboarding-modal/steps/ReceivingRequestsStep.tsx
import React from 'react'
import { Typography, Button, Space, Card } from 'antd'
import { PlayCircleOutlined, LeftOutlined } from '@ant-design/icons'
import { useApproverOnboardingContext } from '../context'
import IntlMessages from '../../../util/IntlMessages'
import capitalizeString from '@vacationtracker/shared/functions/capitalize-string'

const { Title, Text } = Typography

interface ReceivingRequestsStepProps {
  onBack?: () => void
}

export const ReceivingRequestsStep: React.FC<ReceivingRequestsStepProps> = ({ onBack }) => {
  const { nextStep, isLoading, authUser, currentStep } = useApproverOnboardingContext()

  const learnMoreLink = authUser?.platform !== 'slack' && authUser?.platform !== 'microsoft'
    ? 'https://vacationtracker.io/helpdesk/guide-what-can-an-approver-do-in-vacation-tracker/'
    : 'https://www.youtube.com/watch?v=96_HQExSugI'

  return (
    <div className="approver-onboarding-step receiving-requests-step">
      <div className="step-content">
        <div className="step-left-section">
          <div className="request-content-container">
            {/* Back button */}
            {currentStep > 0 && (
              <Button
                type="text"
                icon={<LeftOutlined />}
                onClick={onBack}
                className="step-back-button"
              />
            )}

            <Text type="secondary" className="request-step-indicator">
              1/3
            </Text>
            <Title level={2} className="request-title">
              <IntlMessages id="approver.onboarding.requestApproval.title" />
            </Title>
            <Text className="request-description">
              <IntlMessages id="approver.onboarding.requestApproval.description" />
            </Text>

            <Button
              type="primary"
              size="large"
              onClick={nextStep}
              loading={isLoading}
              className="request-primary-button"
            >
              <IntlMessages id="app.continue" />
            </Button>

            {/* Learn more video card */}
            <Card className="request-video-card">
              <Space align="start" size={16}>
                <a
                  href="https://www.youtube.com/watch?v=96_HQExSugI"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Approving leave video"
                  className="request-video-link"
                >
                  <img
                    src="https://img.youtube.com/vi/96_HQExSugI/hqdefault.jpg"
                    alt="Approving leave video thumbnail"
                    className="request-video-thumbnail"
                  />
                  <PlayCircleOutlined className="request-video-play-icon" />
                </a>
                <div>
                  <Text strong className="request-video-title">
                    <IntlMessages id="approver.onboarding.requestApproval.video.title" values={{ platform: capitalizeString(authUser?.platform || '') }} />
                  </Text>
                  <Text type="secondary" className="request-video-description">
                    <IntlMessages id="approver.onboarding.requestApproval.video.description" />
                  </Text>
                  <Button
                    type="link"
                    className="request-video-learn-more"
                    onClick={() => window.open(learnMoreLink, '_blank', 'noopener')}
                  >
                    <IntlMessages id="approver.onboarding.requestApproval.video.learnMore" />
                  </Button>
                </div>
              </Space>
            </Card>
          </div>
        </div>

        <div className="step-right-section">
          <div className="request-illustration-container">
            <img
              src="/approver-onboarding/ReceivingRequests.svg"
              alt="Request approval illustration"
              className="request-illustration"
            />
          </div>
        </div>
      </div>
    </div>
  )
}